import React from "react";

import { defineMessages, useIntl } from "react-intl";

import { Company, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";

import AddToRouteModal from "@app/component/AddToRouteModal";
import CompanyAssociation from "@app/component/associations/CompanyAssociation";
import DealAssociation from "@app/component/associations/DealAssociation";
import PeopleAssociation from "@app/component/associations/PeopleAssociation";
import RelationshipType from "@app/component/preview/enum/RelationshipType";

import IRelationships from "../../type/IRelationships";

const messages = defineMessages({
  childCompanies: {
    id: "entityPreview.relationships.childCompanies.modal.title",
    defaultMessage: "Select Child Companies",
    description: "Title of a Select Child Companies modal in the Preview Record pane",
  },
  companies: {
    id: "entityPreview.relationships.companies.modal.title",
    defaultMessage: "Select Companies",
    description: "Title of a Select modal in the Preview Record pane",
  },
  company: {
    id: "entityPreview.relationships.company.modal.title",
    defaultMessage: "Select Company",
    description: "Title of a Select modal in the Preview Record pane",
  },
  parentCompany: {
    id: "entityPreview.relationships.parentCompany.modal.title",
    defaultMessage: "Select Parent Company",
    description: "Title of a Select Parent modal in the Preview Record pane",
  },
});

interface Props
  extends Omit<
    IRelationships,
    "deals" | "onSelectDeals" | "onSelectPeople" | "supportedRelationshipTypes"
  > {
  onHide: () => void;
  onHideRouteModal: (added: boolean) => void;
  onSelectCompany?: (ids: Company["id"][], removedIds?: Company["id"][]) => void;
  onSelectDeals?: (ids: Deal["id"][], removedIds?: Deal["id"][]) => void;
  onSelectPeople?: (ids: Person["id"][], removedIds?: Person["id"][]) => void;
  query: string;
  relationshipType?: RelationshipType;
}

const Associations: React.FC<Props> = ({
  childCompanies,
  entity,
  onHide,
  onHideRouteModal,
  onSelectCompany,
  onSelectDeals,
  onSelectPeople,
  onUpdatePrimaryCompany,
  onUpdatePrimaryPerson,
  parentCompany,
  people,
  peopleMultiSelect = true,
  primaryCompanyId,
  primaryPersonId,
  query,
  relationshipType,
}) => {
  const intl = useIntl();

  return (
    <>
      {[RelationshipType.CHILD_COMPANY, RelationshipType.PARENT_COMPANY].includes(
        relationshipType as RelationshipType
      ) && (
        <CompanyAssociation
          assignedCompanies={
            relationshipType === RelationshipType.CHILD_COMPANY
              ? childCompanies
              : entity.entity === EntityType.PERSON
              ? (entity as Person).accounts
              : parentCompany
              ? [parentCompany]
              : undefined
          }
          associateWith={entity.id}
          childSelect={relationshipType === RelationshipType.CHILD_COMPANY}
          defaultQuery={query}
          entityType={entity.entity}
          multiselect={
            RelationshipType.CHILD_COMPANY === relationshipType ||
            entity.entity === EntityType.PERSON
          }
          onHide={onHide}
          onSelect={onSelectCompany}
          onUpdatePrimaryCompany={onUpdatePrimaryCompany}
          primaryCompanyId={primaryCompanyId}
          supportsPrimaryEntity={entity.entity === EntityType.PERSON}
          title={intl.formatMessage(
            relationshipType === RelationshipType.PARENT_COMPANY
              ? entity.entity === EntityType.COMPANY
                ? messages.parentCompany
                : entity.entity === EntityType.PERSON
                ? messages.companies
                : messages.company
              : messages.childCompanies
          )}
        />
      )}
      {relationshipType === RelationshipType.PEOPLE && (
        <PeopleAssociation
          assignedPeople={entity.entity === EntityType.DEAL ? people : undefined}
          defaultQuery={query}
          fixedCompany={entity.entity === EntityType.COMPANY ? entity : undefined}
          multiselect={peopleMultiSelect}
          onHide={onHide}
          onSelect={onSelectPeople}
          onUpdatePrimaryPerson={onUpdatePrimaryPerson}
          primaryPersonId={primaryPersonId}
          supportsPrimaryEntity={entity.entity === EntityType.COMPANY}
        />
      )}
      {relationshipType === RelationshipType.DEALS && (
        <DealAssociation
          defaultQuery={query}
          fixedCompany={entity.entity === EntityType.COMPANY ? entity : undefined}
          fixedPerson={entity.entity === EntityType.PERSON ? entity : undefined}
          multiselect
          onHide={onHide}
          onSelect={onSelectDeals}
        />
      )}
      {relationshipType === RelationshipType.ROUTES && (
        <AddToRouteModal
          autoAssignOnCreate
          entities={[entity] as Company[] | Person[]}
          entityType={(entity as Company | Person).entity}
          onHide={onHideRouteModal}
          visible
        />
      )}
    </>
  );
};

export default Associations;
